import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { PureAbility } from '@casl/ability';
import { AbilityService } from '@casl/angular';
import * as store from '@daas-consulting/production/shared/frontend/store';
import { LayoutBase2PageViewContract } from '@daas-consulting/production/shared/frontend/view_contracts';
import {
  AuthenticationViewContract,
  SecurityAuthenticationViewContractId,
} from '@daas-consulting/security/authentication/frontend/view_contracts';
import { Select } from '@ngxs/store';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

@Injectable()
export class LayoutBase2PageViewModel
  implements LayoutBase2PageViewContract, OnDestroy
{
  @Select(store.loader.selectors.LoaderSelectors.isLoading)
  isLoading$!: Observable<boolean>;

  readonly name$!: Observable<string>;
  readonly email$!: Observable<string>;
  readonly picture$!: Observable<string>;
  readonly version$: Observable<string>;
  readonly display$: Observable<string>;
  readonly showCreatePrescription$: Observable<boolean>;
  readonly showListPrescription$: Observable<boolean>;
  readonly showListPatient$: Observable<boolean>;
  readonly showListMedic$: Observable<boolean>;
  readonly showListInstitution$: Observable<boolean>;
  readonly showListUsers$: Observable<boolean>;
  readonly showListConsumption$: Observable<boolean>;
  readonly showListCompound$: Observable<boolean>;
  readonly showListLine$: Observable<boolean>;
  readonly showListFinancier$: Observable<boolean>;
  readonly showListArticle$: Observable<boolean>;
  readonly showListProduct$: Observable<boolean>;
  readonly showListPurchaseOrder$: Observable<boolean>;
  readonly showListRemito$: Observable<boolean>;
  readonly showListSemaphore$: Observable<boolean>;
  readonly showListPrescriptionReport$: Observable<boolean>;
  readonly showListPrescriptionReportSummary$: Observable<boolean>;
  readonly showListRemitosReport$: Observable<boolean>;

  readonly #showCreatePrescription$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  readonly #showListPrescription$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  readonly #showListPatient$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListMedic$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListInstitution$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  readonly #showListUsers$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListConsumption$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  readonly #showListCompound$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListLine$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListFinancier$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListArticle$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListProduct$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListPurchaseOrder$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  readonly #showListRemito$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListSemaphore$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  readonly #showListPrescriptionReport$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  readonly #showListRemitosReport$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly #showListPrescriptionReportSummary$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  readonly #router: Router;
  readonly #viewModel: AuthenticationViewContract;
  readonly #destroy$: Subject<void> = new Subject<void>();

  constructor(
    router: Router,
    abilityService: AbilityService<PureAbility>,
    @Inject(SecurityAuthenticationViewContractId)
    viewModel: AuthenticationViewContract
  ) {
    this.#router = router;
    this.#viewModel = viewModel;
    this.name$ = this.#viewModel.name$;
    this.email$ = this.#viewModel.email$;
    this.picture$ = this.#viewModel.picture$;
    this.version$ = this.#viewModel.version$;
    this.display$ = this.#viewModel.display$;

    this.showCreatePrescription$ = this.#showCreatePrescription$.asObservable();
    this.showListPrescription$ = this.#showListPrescription$.asObservable();
    this.showListPatient$ = this.#showListPatient$.asObservable();
    this.showListMedic$ = this.#showListMedic$.asObservable();
    this.showListInstitution$ = this.#showListInstitution$.asObservable();
    this.showListUsers$ = this.#showListUsers$.asObservable();
    this.showListConsumption$ = this.#showListConsumption$.asObservable();
    this.showListCompound$ = this.#showListCompound$.asObservable();
    this.showListLine$ = this.#showListLine$.asObservable();
    this.showListFinancier$ = this.#showListFinancier$.asObservable();
    this.showListArticle$ = this.#showListArticle$.asObservable();
    this.showListProduct$ = this.#showListProduct$.asObservable();
    this.showListPurchaseOrder$ = this.#showListPurchaseOrder$.asObservable();
    this.showListRemito$ = this.#showListRemito$.asObservable();
    this.showListSemaphore$ = this.#showListSemaphore$.asObservable();
    this.showListPrescriptionReport$ =
      this.#showListPrescriptionReport$.asObservable();
    this.showListPrescriptionReportSummary$ =
      this.#showListPrescriptionReportSummary$.asObservable();
    this.showListRemitosReport$ = this.#showListRemitosReport$.asObservable();

    abilityService.ability$
      .pipe(takeUntil(this.#destroy$))
      .subscribe((ability) => {
        const createPrescription = ability.can(
          'create',
          'prescriptions.prescriptions.prescription'
        );
        this.#showCreatePrescription$.next(createPrescription);
        const listPrescription = ability.can(
          'list',
          'prescriptions.prescriptions.prescription'
        );
        this.#showListPrescription$.next(listPrescription);
        const listPatient = ability.can('list', 'patients.patients.patient');
        this.#showListPatient$.next(listPatient);
        const listMedic = ability.can('list', 'institutions.medics.medic');
        this.#showListMedic$.next(listMedic);
        const listInstitution = ability.can(
          'list',
          'institutions.institutions.institution'
        );
        this.#showListInstitution$.next(listInstitution);
        const listUsers = ability.can('list', 'security.users.user');
        this.#showListUsers$.next(listUsers);
        const listConsumption = ability.can(
          'list',
          'prescriptions.prescriptions.consumption'
        );
        this.#showListConsumption$.next(listConsumption);
        const listCompound = ability.can(
          'list',
          'production.compounds.compound'
        );
        this.#showListCompound$.next(listCompound);
        const listLine = ability.can('list', 'production.lines.line');
        this.#showListLine$.next(listLine);
        const listFinancier = ability.can(
          'list',
          'production.financiers.financier'
        );
        this.#showListFinancier$.next(listFinancier);
        const listArticle = ability.can('list', 'production.articles.article');
        this.#showListArticle$.next(listArticle);
        const listProduct = ability.can('list', 'production.products.product');
        this.#showListProduct$.next(listProduct);
        const listPurchaseOrder = ability.can(
          'list',
          'production.purchase_orders.purchase_order'
        );
        this.#showListPurchaseOrder$.next(listPurchaseOrder);
        const listRemito = ability.can('list', 'production.remitos.remito');
        this.#showListRemito$.next(listRemito);
        const listSemaphore = ability.can('list', 'production.remitos.remito');
        this.#showListSemaphore$.next(listSemaphore);
        const listPrescriptionReport = ability.can(
          'list',
          'production.prescription_report.report'
        );
        this.#showListPrescriptionReport$.next(listPrescriptionReport);
        const listPrescriptionReportSummary = ability.can(
          'list',
          'production.prescription_report.summary'
        );
        const listRemitosReport = ability.can(
          'list',
          'production.remitos_report.report'
        );
        this.#showListRemitosReport$.next(listRemitosReport);
        this.#showListPrescriptionReportSummary$.next(
          listPrescriptionReportSummary
        );
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  async logout(): Promise<void> {
    await this.#viewModel.logout();
  }

  isMedicsRouteActive(): boolean {
    const rv = /\/institutions\/medics/i.test(this.#router.url);
    return rv;
  }

  isInstitutionsRouteActive(): boolean {
    const rv = /\/institutions\/(?:form|list)/i.test(this.#router.url);
    return rv;
  }
}
